import type {
	Action,
	BoundActions,
	HookFunction,
	HookStateFunction,
	OverrideContainerComponent,
} from 'react-sweet-state';
import {
	createActionsHook,
	createHook,
	createStore,
	createContainer,
	createStateHook,
} from 'react-sweet-state';
import { useCallback } from 'react';

import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { ADFEntity } from '@atlaskit/adf-utils/types';

import type { AiIssueCreateState } from '@atlassian/ai-issue-create/types';
import type { AvailableSite } from '@atlassian/gic-anywhere/utils';

import { expVal } from '@confluence/feature-experiments';

import type { InsertStorageFragmentRequestPayload } from '../__types__/InsertStorageFragmentRequestPayload.ts';
import type {
	CreateIssueBodyIssueLinkType,
	CreateIssueResponse,
	FieldMetadata,
	JiraProject,
	SupportedValueType,
	TransitionResponseItems,
} from '../__types__/apiUtils.ts';
import type { InsertStorageColumnTableRequestPayload } from '../__types__/InsertStorageColumnTableRequestPayload.ts';
import { transformStargateUrl } from '../utils/configureUtils.ts';
import { messages } from '../messages.ts';

import {
	useAiBulkNavigationGotoPage,
	type AiBulkNavigationPageType,
} from './AiBulkNavigationContextProvider.tsx';

export const AiStates = {
	ABORT: 'abort',
	LOADING: 'loading',
	ERROR: 'error',
	IDLE: 'idle',
} as const;

export type ManualCreateStates = 'loading' | 'error' | 'idle';

export const ErrorStates = {
	BULK_CREATION: 'bulk-issue-creation-failed',
	ISSUE_GENERATION: 'ai-issue-generation-failed',
	SITE_RETRIEVAL: 'jira-site-retreival-failed',
	PROJECT_RETRIEVAL: 'jira-project-retreival-failed',
	ISSUE_TYPE_RETRIEVAL: 'issue-type-retreival-failed',
	EMPTY: '',
} as const;

export type ErrorState = (typeof ErrorStates)[keyof typeof ErrorStates];
type NonEmptyErrorState = Exclude<ErrorState, typeof ErrorStates.EMPTY>;
export const ErrorMessageMap: Record<
	NonEmptyErrorState,
	() => {
		id: string;
		defaultMessage: string;
		description: string;
	}
> = ((): Record<
	NonEmptyErrorState,
	() => {
		id: string;
		defaultMessage: string;
		description: string;
	}
> => {
	return {
		[ErrorStates.BULK_CREATION]: () => messages.bulkCreateAiErrorDescriptionRebrandToRovo,
		[ErrorStates.ISSUE_GENERATION]: () => messages.issueGenerationErrorDescriptionRebrandToRovo,
		[ErrorStates.SITE_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
		[ErrorStates.PROJECT_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
		[ErrorStates.ISSUE_TYPE_RETRIEVAL]: () =>
			expVal('project-terminology-refresh', 'isEnabled', false)
				? messages.connectJiraProjectErrorDescriptionGalaxia
				: messages.connectJiraProjectErrorDescription,
	};
})();
export interface IssueType {
	iconUrl: string;
	name: string;
	id: string;
	description?: string;
	self?: string;
	subtask?: boolean;
}

export interface IssueObject {
	id: string;
	summary: string;
	description?: DocNode;
	issueType: IssueType;
	parentIssueLink?: string;
	assigneeUserId?: string;
	labels?: string[];
	parent?: CreateIssueBodyIssueLinkType;
	// Versions must be flattened down to just an array of IDs, but we need to know the field name as well.
	additionalFields?: AdditionalIssueField[];
	rowIndex?: number;
}

export interface AdditionalIssueField {
	field: FieldMetadata;
	value: SupportedValueType;
}

type IssueTypeFields = {
	requiredFields: FieldMetadata[];
	optionalFields: FieldMetadata[];
};

// Still not fully known what the exact object shape that will be sent to us is
export type BulkIssuesListType = IssueObject[];

export type BulkCreateContextType = {
	bulkIssuesList: BulkIssuesListType;
	contentLastModifiedTimestamp?: string;
	insertStorageFragmentRequestPayload?: InsertStorageFragmentRequestPayload;
	insertStorageColumnTableRequestPayload?: InsertStorageColumnTableRequestPayload;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	isCreatedFromTable: boolean;
	isCreatedFromActionItems: boolean;
	currentJiraSite: AvailableSite;
	currentJiraProject: JiraProject;
	currentIssueId: string;
	issueTypeHasRequiredFields: boolean;
	singleIssuesCreated: CreateIssueResponse[];
	error: ErrorState;
	issueTypeFields: IssueTypeFields;
	manualCreateState: ManualCreateStates;
	aiResultViewedFiredOnce: boolean;
	expectedIssueIndices: number[];
	selectedTableAdf?: ADFEntity;
};

type State = {
	bulkIssuesList: BulkIssuesListType;
	currentIssueId: string;
	contentLastModifiedTimestamp: string | null | undefined;
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
	insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload | undefined;
	aiState: AiIssueCreateState;
	isCreatedWithAi: boolean;
	isCreatedFromTable: boolean;
	isCreatedFromActionItems: boolean;
	currentJiraSite: AvailableSite | undefined;
	currentJiraProject: JiraProject | undefined;
	currentDefaultIssueType: IssueType | undefined;
	issueTypeHasRequiredFields: boolean;
	singleIssuesCreated: CreateIssueResponse[];
	error: ErrorState;
	issueTypeFields: IssueTypeFields;
	manualCreateState: ManualCreateStates;
	aiResultViewedFiredOnce: boolean;
	closeAndAddIssuesToPage: boolean;
	expectedIssueIndices: number[];
	selectedTableAdf: ADFEntity | undefined; // The table ADF that was selected for bulk create; will only be defined when selected in the editor
	userChoseManualCreation: boolean;
	workflowValidators: TransitionResponseItems | null;
	isRovoPanelWidth: boolean;
};

export type IssueCreateSidePanelActions = BoundActions<State, typeof bulkCreateActions>;

// Persisted state: survives panel close/reopen to maintain the user's Jira configuration
// and track issues that need to be inserted into the page.
const initialPersistedBulkCreateState = {
	currentJiraSite: undefined as State['currentJiraSite'],
	currentJiraProject: undefined as State['currentJiraProject'],
	currentDefaultIssueType: undefined as State['currentDefaultIssueType'],
	issueTypeHasRequiredFields: false,
	singleIssuesCreated: [] as State['singleIssuesCreated'],
	issueTypeFields: {
		requiredFields: [],
		optionalFields: [],
	} as State['issueTypeFields'],
};

// Transient state: reset on panel close
// Workflow-specific state for the current session
const initialTransientBulkCreateState = {
	bulkIssuesList: [] as State['bulkIssuesList'],
	currentIssueId: '',
	contentLastModifiedTimestamp: undefined as State['contentLastModifiedTimestamp'],
	insertStorageFragmentRequestPayload: undefined as State['insertStorageFragmentRequestPayload'],
	insertStorageColumnTableRequestPayload:
		undefined as State['insertStorageColumnTableRequestPayload'],
	aiState: AiStates.IDLE as State['aiState'],
	isCreatedWithAi: false,
	isCreatedFromTable: false,
	isCreatedFromActionItems: false,
	error: '' as ErrorState,
	manualCreateState: 'idle' as ManualCreateStates,
	aiResultViewedFiredOnce: false,
	closeAndAddIssuesToPage: false,
	expectedIssueIndices: [] as State['expectedIssueIndices'],
	selectedTableAdf: undefined as State['selectedTableAdf'],
	userChoseManualCreation: false,
	workflowValidators: null as State['workflowValidators'],
	isRovoPanelWidth: false,
};

const initialBulkCreateState: State = {
	...initialPersistedBulkCreateState,
	...initialTransientBulkCreateState,
};

const bulkCreateActions = {
	setCurrentIssueId:
		(currentIssueId: State['currentIssueId']): Action<State> =>
		({ setState }) => {
			setState({ currentIssueId });
		},
	setBulkIssuesList:
		(bulkIssuesList: State['bulkIssuesList']): Action<State> =>
		({ setState }) => {
			setState({ bulkIssuesList });
		},
	setIssueTypeFields:
		(issueTypeFields: State['issueTypeFields']): Action<State> =>
		({ setState }) => {
			setState({ issueTypeFields });
		},
	setAiState:
		(aiState: State['aiState']): Action<State> =>
		({ setState, getState }) => {
			const { error } = getState();
			if (error === ErrorStates.ISSUE_GENERATION && aiState == AiStates.LOADING) {
				setState({ aiState, error: ErrorStates.EMPTY });
			} else if (aiState == AiStates.ERROR) {
				setState({ aiState, error: ErrorStates.ISSUE_GENERATION });
			} else {
				setState({ aiState });
			}
		},
	setIsCreatedWithAi:
		(isCreatedWithAi: State['isCreatedWithAi']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedWithAi });
		},
	setIsCreatedFromTable:
		(isCreatedFromTable: State['isCreatedFromTable']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedFromTable });
		},
	setIsCreatedFromActionItems:
		(isCreatedFromActionItems: State['isCreatedFromActionItems']): Action<State> =>
		({ setState }) => {
			setState({ isCreatedFromActionItems });
		},
	setContentLastModifiedTimestamp:
		(contentLastModifiedTimestamp: State['contentLastModifiedTimestamp']): Action<State> =>
		({ setState }) => {
			setState({ contentLastModifiedTimestamp });
		},
	setInsertStorageFragmentRequestPayload:
		(insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload): Action<State> =>
		({ setState }) => {
			setState({ insertStorageFragmentRequestPayload });
		},
	setInsertStorageColumnTableRequestPayload:
		(
			insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload,
		): Action<State> =>
		({ setState }) => {
			setState({ insertStorageColumnTableRequestPayload });
		},
	removeBulkIssue:
		(issueId: string): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList } = getState();
			const newBulkIssuesList = bulkIssuesList.filter((issue) => issue.id !== issueId);
			setState({
				bulkIssuesList: newBulkIssuesList,
				closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
			});
		},
	removeBulkIssueList:
		(issueIds: string[]): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList } = getState();
			setState({
				bulkIssuesList: bulkIssuesList.filter((issue) => !issueIds.includes(issue.id)),
			});
		},
	setSingleIssuesCreated:
		(singleIssue: CreateIssueResponse): Action<State> =>
		({ setState, getState }) => {
			const { singleIssuesCreated } = getState();
			setState({
				singleIssuesCreated: [...singleIssuesCreated, singleIssue],
			});
		},
	resetSingleIssuesCreated:
		(): Action<State> =>
		({ setState }) => {
			setState({
				singleIssuesCreated: initialBulkCreateState.singleIssuesCreated,
			});
		},
	setCurrentJiraSite:
		(currentJiraSite: State['currentJiraSite']): Action<State> =>
		({ setState }) => {
			setState({
				currentJiraSite,
				workflowValidators: null,
			});
		},
	setCurrentJiraProject:
		(currentJiraProject: State['currentJiraProject']): Action<State> =>
		({ setState }) => {
			setState({
				currentJiraProject,
				workflowValidators: null,
			});
		},
	setNextIssueToEdit:
		(goToPage: (page: AiBulkNavigationPageType) => void): Action<State> =>
		({ setState, getState }) => {
			const { bulkIssuesList, currentIssueId } = getState();
			const currentIssueListIndex = bulkIssuesList.findIndex(
				(issue) => issue?.id === currentIssueId,
			);
			const newBulkIssuesList = bulkIssuesList.filter((issue) => issue?.id !== currentIssueId);

			if (currentIssueListIndex >= bulkIssuesList.length - 1) {
				setState({
					bulkIssuesList: newBulkIssuesList,
					closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
				});
				void goToPage('default');
				return;
			}
			setState({
				currentIssueId: bulkIssuesList[currentIssueListIndex + 1]?.id,
				bulkIssuesList: newBulkIssuesList,
				closeAndAddIssuesToPage: newBulkIssuesList.length === 0,
			});
		},
	setCurrentDefaultIssueType:
		(currentDefaultIssueType: State['currentDefaultIssueType']): Action<State> =>
		({ setState }) => {
			setState({
				currentDefaultIssueType,
				workflowValidators: null,
			});
		},
	setIssueTypeHasRequiredFields:
		(issueTypeHasRequiredFields: State['issueTypeHasRequiredFields']): Action<State> =>
		({ setState }) => {
			setState({ issueTypeHasRequiredFields });
		},
	setError:
		(error: State['error']): Action<State> =>
		({ setState }) => {
			setState({ error });
		},
	setManualCreateState:
		(manualCreateState: State['manualCreateState']): Action<State> =>
		({ setState, getState }) => {
			const { error } = getState();
			const isManualError =
				error === ErrorStates.SITE_RETRIEVAL ||
				error === ErrorStates.PROJECT_RETRIEVAL ||
				error === ErrorStates.ISSUE_TYPE_RETRIEVAL;
			if (isManualError && manualCreateState === 'loading') {
				setState({ manualCreateState, error: ErrorStates.EMPTY });
			} else if (manualCreateState === 'error') {
				setState({ manualCreateState, error: ErrorStates.SITE_RETRIEVAL }); // Can be site , project or issue type retrieval since they all have the same error message
			} else {
				setState({ manualCreateState });
			}
		},
	// Resets transient bulk create state while preserving Jira configuration
	resetTransientBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState(initialTransientBulkCreateState);
		},
	resetManualBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState({ manualCreateState: 'idle', error: ErrorStates.EMPTY, bulkIssuesList: [] });
		},
	resetAIBulkCreateState:
		(): Action<State> =>
		({ setState }) => {
			setState({ bulkIssuesList: [], aiResultViewedFiredOnce: false });
		},
	setAiResultViewedFiredOnce:
		(aiResultViewedFiredOnce: State['aiResultViewedFiredOnce']): Action<State> =>
		({ setState }) => {
			setState({ aiResultViewedFiredOnce });
		},
	resetCloseAndAddIssuesToPage:
		(): Action<State> =>
		({ setState }) => {
			setState({
				closeAndAddIssuesToPage: false,
			});
		},
	setExpectedIssueIndices:
		(expectedIssueIndices: State['expectedIssueIndices']): Action<State> =>
		({ setState }) => {
			setState({ expectedIssueIndices });
		},
	setSelectedTableAdf:
		(selectedTableAdf: State['selectedTableAdf']): Action<State> =>
		({ setState }) => {
			setState({ selectedTableAdf });
		},
	setUserChoseManualCreation:
		(userChoseManualCreation: State['userChoseManualCreation']): Action<State> =>
		({ setState }) => {
			setState({ userChoseManualCreation });
		},
	setWorkflowValidators:
		(workflowValidators: State['workflowValidators']): Action<State> =>
		({ setState }) => {
			setState({ workflowValidators });
		},
	setIsRovoPanelWidth:
		(isRovoPanelWidth: State['isRovoPanelWidth']): Action<State> =>
		({ setState }) => {
			setState({ isRovoPanelWidth });
		},
};

const Store = createStore({
	initialState: {
		...initialBulkCreateState,
	},
	actions: bulkCreateActions,
	name: 'bulkCreateContext',
});

export const BulkCreateContainer: OverrideContainerComponent<unknown> = createContainer(Store);

export const useBulkCreateContext: HookFunction<
	{
		bulkIssuesList: BulkIssuesListType;
		currentIssueId: string;
		contentLastModifiedTimestamp: string | null | undefined;
		insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
		insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload | undefined;
		aiState: AiIssueCreateState;
		isCreatedWithAi: boolean;
		isCreatedFromTable: boolean;
		isCreatedFromActionItems: boolean;
		currentJiraSite: AvailableSite | undefined;
		currentJiraProject: JiraProject | undefined;
		currentDefaultIssueType: IssueType | undefined;
		issueTypeHasRequiredFields: boolean;
		singleIssuesCreated: CreateIssueResponse[];
		error: ErrorState;
		issueTypeFields: IssueTypeFields;
		manualCreateState: ManualCreateStates;
		aiResultViewedFiredOnce: boolean;
		closeAndAddIssuesToPage: boolean;
		expectedIssueIndices: number[];
		selectedTableAdf: ADFEntity | undefined; // The table ADF that was selected for bulk create; will only be defined when selected in the editor
		userChoseManualCreation: boolean;
		workflowValidators: TransitionResponseItems | null;
		isRovoPanelWidth: boolean;
	},
	BoundActions<
		{
			bulkIssuesList: BulkIssuesListType;
			currentIssueId: string;
			contentLastModifiedTimestamp: string | null | undefined;
			insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload | undefined;
			insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload | undefined;
			aiState: AiIssueCreateState;
			isCreatedWithAi: boolean;
			isCreatedFromTable: boolean;
			isCreatedFromActionItems: boolean;
			currentJiraSite: AvailableSite | undefined;
			currentJiraProject: JiraProject | undefined;
			currentDefaultIssueType: IssueType | undefined;
			issueTypeHasRequiredFields: boolean;
			singleIssuesCreated: CreateIssueResponse[];
			error: ErrorState;
			issueTypeFields: IssueTypeFields;
			manualCreateState: ManualCreateStates;
			aiResultViewedFiredOnce: boolean;
			closeAndAddIssuesToPage: boolean;
			expectedIssueIndices: number[];
			selectedTableAdf: ADFEntity | undefined; // The table ADF that was selected for bulk create; will only be defined when selected in the editor
			userChoseManualCreation: boolean;
			workflowValidators: TransitionResponseItems | null;
			isRovoPanelWidth: boolean;
		},
		{
			setCurrentIssueId: (currentIssueId: State['currentIssueId']) => Action<State>;
			setBulkIssuesList: (bulkIssuesList: State['bulkIssuesList']) => Action<State>;
			setIssueTypeFields: (issueTypeFields: State['issueTypeFields']) => Action<State>;
			setAiState: (aiState: State['aiState']) => Action<State>;
			setIsCreatedWithAi: (isCreatedWithAi: State['isCreatedWithAi']) => Action<State>;
			setIsCreatedFromTable: (isCreatedFromTable: State['isCreatedFromTable']) => Action<State>;
			setIsCreatedFromActionItems: (
				isCreatedFromActionItems: State['isCreatedFromActionItems'],
			) => Action<State>;
			setContentLastModifiedTimestamp: (
				contentLastModifiedTimestamp: State['contentLastModifiedTimestamp'],
			) => Action<State>;
			setInsertStorageFragmentRequestPayload: (
				insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload,
			) => Action<State>;
			setInsertStorageColumnTableRequestPayload: (
				insertStorageColumnTableRequestPayload: InsertStorageColumnTableRequestPayload,
			) => Action<State>;
			removeBulkIssue: (issueId: string) => Action<State>;
			removeBulkIssueList: (issueIds: string[]) => Action<State>;
			setSingleIssuesCreated: (singleIssue: CreateIssueResponse) => Action<State>;
			resetSingleIssuesCreated: () => Action<State>;
			setCurrentJiraSite: (currentJiraSite: State['currentJiraSite']) => Action<State>;
			setCurrentJiraProject: (currentJiraProject: State['currentJiraProject']) => Action<State>;
			setNextIssueToEdit: (goToPage: (page: AiBulkNavigationPageType) => void) => Action<State>;
			setCurrentDefaultIssueType: (
				currentDefaultIssueType: State['currentDefaultIssueType'],
			) => Action<State>;
			setIssueTypeHasRequiredFields: (
				issueTypeHasRequiredFields: State['issueTypeHasRequiredFields'],
			) => Action<State>;
			setError: (error: State['error']) => Action<State>;
			setManualCreateState: (manualCreateState: State['manualCreateState']) => Action<State>;
			// Resets transient bulk create state while preserving Jira configuration
			resetTransientBulkCreateState: () => Action<State>;
			resetManualBulkCreateState: () => Action<State>;
			resetAIBulkCreateState: () => Action<State>;
			setAiResultViewedFiredOnce: (
				aiResultViewedFiredOnce: State['aiResultViewedFiredOnce'],
			) => Action<State>;
			resetCloseAndAddIssuesToPage: () => Action<State>;
			setExpectedIssueIndices: (
				expectedIssueIndices: State['expectedIssueIndices'],
			) => Action<State>;
			setSelectedTableAdf: (selectedTableAdf: State['selectedTableAdf']) => Action<State>;
			setUserChoseManualCreation: (
				userChoseManualCreation: State['userChoseManualCreation'],
			) => Action<State>;
			setWorkflowValidators: (workflowValidators: State['workflowValidators']) => Action<State>;
			setIsRovoPanelWidth: (isRovoPanelWidth: State['isRovoPanelWidth']) => Action<State>;
		}
	>,
	void
> = createHook(Store);

const useActions = createActionsHook(Store);

export const useSetIssueTypeFields = (): ((
	issueTypeFields: IssueTypeFields,
) => void | Promise<void>) => {
	const { setIssueTypeFields } = useActions();
	return setIssueTypeFields;
};

export const useSetIsCreatedWithAi = (): ((isCreatedWithAi: boolean) => void | Promise<void>) => {
	const { setIsCreatedWithAi } = useActions();
	return setIsCreatedWithAi;
};

export const useSetIsCreatedFromTable = (): ((
	isCreatedFromTable: boolean,
) => void | Promise<void>) => {
	const { setIsCreatedFromTable } = useActions();
	return setIsCreatedFromTable;
};

export const useSetIsCreatedFromActionItems = (): ((
	isCreatedFromActionItems: boolean,
) => void | Promise<void>) => {
	const { setIsCreatedFromActionItems } = useActions();
	return setIsCreatedFromActionItems;
};

export const useSetCurrentIssueId = (): ((currentIssueId: string) => void | Promise<void>) => {
	const { setCurrentIssueId } = useActions();
	return setCurrentIssueId;
};

export const useRemoveBulkIssue = (): ((issueId: string) => void | Promise<void>) => {
	const { removeBulkIssue } = useActions();
	return removeBulkIssue;
};

export const useSetNextIssueToEdit = (): (() => undefined) => {
	const { setNextIssueToEdit } = useActions();
	const goToPage = useAiBulkNavigationGotoPage();
	return useCallback(() => void setNextIssueToEdit(goToPage), [goToPage, setNextIssueToEdit]);
};

export const useSetIssueTypeHasRequiredFields = (): ((
	issueTypeHasRequiredFields: boolean,
) => void | Promise<void>) => {
	const { setIssueTypeHasRequiredFields } = useActions();
	return setIssueTypeHasRequiredFields;
};

export const useSetSingleIssuesCreated = (): ((
	singleIssue: CreateIssueResponse,
) => void | Promise<void>) => {
	const { setSingleIssuesCreated } = useActions();
	return setSingleIssuesCreated;
};

export const useResetTransientBulkCreateState = (): (() => void | Promise<void>) => {
	const { resetTransientBulkCreateState } = useActions();
	return resetTransientBulkCreateState;
};

export const useSetManualCreateState = (): ((
	manualCreateState: ManualCreateStates,
) => void | Promise<void>) => {
	const { setManualCreateState } = useActions();
	return setManualCreateState;
};

export const useResetManualBulkCreateState = (): (() => void | Promise<void>) => {
	const { resetManualBulkCreateState } = useActions();
	return resetManualBulkCreateState;
};

export const useResetSingleIssuesCreated = (): (() => void | Promise<void>) => {
	const { resetSingleIssuesCreated } = useActions();
	return resetSingleIssuesCreated;
};

export const useGetContentLastModifiedTimestamp: HookStateFunction<
	string | null | undefined,
	void
> = createStateHook(Store, {
	selector: (state: State): string | null | undefined => state.contentLastModifiedTimestamp,
});

export const useSetContentLastModifiedTimestamp = (): ((
	contentLastModifiedTimestamp: string | null | undefined,
) => void | Promise<void>) => {
	const { setContentLastModifiedTimestamp } = useActions();
	return setContentLastModifiedTimestamp;
};

export const useSetInsertStorageFragmentRequestPayload = (): ((
	insertStorageFragmentRequestPayload: InsertStorageFragmentRequestPayload,
) => void | Promise<void>) => {
	const { setInsertStorageFragmentRequestPayload } = useActions();
	return setInsertStorageFragmentRequestPayload;
};

export const useGetIssueType: HookStateFunction<IssueType, void> = createStateHook(Store, {
	selector: (state: State): IssueType => ({
		iconUrl: transformStargateUrl(state.currentDefaultIssueType?.iconUrl || ''),
		name: state.currentDefaultIssueType?.name || '',
		id: state.currentDefaultIssueType?.id || '',
	}),
});

export const useGetIsMissingColumnMappingError: HookStateFunction<boolean, void> = createStateHook(
	Store,
	{
		selector: (state: State): boolean =>
			!state.isCreatedWithAi && state.bulkIssuesList.length === 0,
	},
);

export const useBulkIssuesList: HookStateFunction<BulkIssuesListType, void> = createStateHook(
	Store,
	{
		selector: (state: State) => state.bulkIssuesList,
	},
);

export const useIsCreatedWithAi: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state: State) => state.isCreatedWithAi,
});

export const useSetError = (): ((error: ErrorState) => void | Promise<void>) => {
	const { setError } = useActions();
	return setError;
};

export const useCloseAndAddIssuesToPage: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state: State): boolean => state.closeAndAddIssuesToPage,
});

export const useResetCloseAndAddIssuesToPage = (): (() => void | Promise<void>) => {
	const { resetCloseAndAddIssuesToPage } = useActions();
	return resetCloseAndAddIssuesToPage;
};

export const useSetBulkIssuesList = (): ((
	bulkIssuesList: BulkIssuesListType,
) => void | Promise<void>) => {
	const { setBulkIssuesList } = useActions();
	return setBulkIssuesList;
};

export const useSetExpectedIssueIndices = (): ((
	expectedIssueIndices: number[],
) => void | Promise<void>) => {
	const { setExpectedIssueIndices } = useActions();
	return setExpectedIssueIndices;
};

export const useSetSelectedTableAdf = (): ((
	selectedTableAdf: ADFEntity | undefined,
) => void | Promise<void>) => {
	const { setSelectedTableAdf } = useActions();
	return setSelectedTableAdf;
};

export const useSetUserChoseManualCreation = (): ((
	userChoseManualCreation: boolean,
) => void | Promise<void>) => {
	const { setUserChoseManualCreation } = useActions();
	return setUserChoseManualCreation;
};

export const useSetCurrentJiraSite = (): ((
	currentJiraSite: AvailableSite | undefined,
) => void | Promise<void>) => {
	const { setCurrentJiraSite } = useActions();
	return setCurrentJiraSite;
};

export const useGetJiraConfig: HookStateFunction<
	{
		currentJiraSite: AvailableSite | undefined;
		currentJiraProject: JiraProject | undefined;
		currentDefaultIssueType: IssueType | undefined;
	},
	void
> = createStateHook(Store, {
	selector: (state: State) => ({
		currentJiraSite: state.currentJiraSite,
		currentJiraProject: state.currentJiraProject,
		currentDefaultIssueType: state.currentDefaultIssueType,
	}),
});

export const useGetWorkflowValidators: HookStateFunction<TransitionResponseItems | null, void> =
	createStateHook(Store, {
		selector: (state: State) => state.workflowValidators,
	});

export const useSetWorkflowValidators = (): ((
	workflowValidators: TransitionResponseItems | null,
) => void | Promise<void>) => {
	const { setWorkflowValidators } = useActions();
	return setWorkflowValidators;
};

export const useIsRovoPanelWidth: HookStateFunction<boolean, void> = createStateHook(Store, {
	selector: (state: State) => state.isRovoPanelWidth,
});

export const useSetIsRovoPanelWidth = (): ((isRovoPanelWidth: boolean) => void | Promise<void>) => {
	const { setIsRovoPanelWidth } = useActions();
	return setIsRovoPanelWidth;
};

export const InitiativeWorkItemTypeName = 'Initiative';
export const EpicWorkItemTypeName = 'Epic';
